import { useMemo, useState, type ReactNode } from 'react'
import {
  AmountInput,
  Button,
  confirmDialog,
  DataTable,
  DatePicker,
  Drawer,
  Icon,
  Money,
  SearchSelect,
  type ColumnDef,
  type IconName,
} from '../components'
import { useMe } from '../lib/use-me'
import { canViewExecutive } from '../lib/permissions'
import { ApiError } from '../lib/api'
import { useAppState } from '../app-state'
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizations,
  useUpdateOrganization,
} from '../lib/api/organizations'
import {
  useAccounts,
  useCreateAccount,
  useDeleteAccount,
  useUpdateAccount,
} from '../lib/api/accounts'
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '../lib/api/categories'
import {
  useCreateProject,
  useDeleteProject,
  useProjects,
  useUpdateProject,
} from '../lib/api/projects'
import { useCreateTag, useDeleteTag, useTags, useUpdateTag } from '../lib/api/tags'
import {
  useCounterparties,
  useCreateCounterparty,
  useDeleteCounterparty,
  useUpdateCounterparty,
} from '../lib/api/counterparties'
import {
  flattenEmployees,
  useCreateEmployee,
  useDeleteEmployee,
  useEmployees,
  usePatchEmployee,
} from '../lib/api/employees'
import {
  flattenFxRates,
  useCreateFxRate,
  useDeleteFxRate,
  useFxRates,
  useLatestFxRate,
  useRefreshFxRates,
} from '../lib/api/fx-rates'
import {
  useCreateLicenseTariff,
  useDeleteLicenseTariff,
  useLicenseTariffs,
  useUpdateLicenseTariff,
} from '../lib/api/license-tariffs'
import { TelegramSection } from './settings/telegram-section'
import { AuditLogSection } from './settings/audit-log-section'
import { ClientErrorsSection } from './settings/client-errors-section'
import type {
  Account,
  AccountType,
  Category,
  CategoryKind,
  Counterparty,
  CounterpartyType,
  Currency,
  Employee,
  FxRate,
  FxRateSource,
  LicenseTariff,
  Organization,
  Project,
  Tag,
} from '../lib/api/types'

type SectionId =
  | 'organizations'
  | 'accounts'
  | 'categories'
  | 'projects'
  | 'tags'
  | 'counterparties'
  | 'employees'
  | 'fx-rates'
  | 'license-tariffs'
  | 'telegram'
  | 'audit-log'
  | 'client-errors'

const SECTIONS: { id: SectionId; icon: IconName; label: string; adminOnly?: boolean }[] = [
  { id: 'organizations', icon: 'briefcase', label: 'Організації' },
  { id: 'accounts', icon: 'landmark', label: 'Рахунки' },
  { id: 'categories', icon: 'folder-tree', label: 'Категорії' },
  { id: 'projects', icon: 'folder-open', label: 'Проєкти' },
  { id: 'tags', icon: 'tag', label: 'Теги' },
  { id: 'counterparties', icon: 'users', label: 'Контрагенти' },
  { id: 'employees', icon: 'user-round', label: 'Співробітники' },
  { id: 'fx-rates', icon: 'coins', label: 'Курси валют' },
  { id: 'license-tariffs', icon: 'key-round', label: 'Тарифи ліцензій' },
  { id: 'telegram', icon: 'send', label: 'Telegram' },
  { id: 'audit-log', icon: 'history', label: 'Лог змін', adminOnly: true },
  { id: 'client-errors', icon: 'bug', label: 'Помилки інтерфейсу', adminOnly: true },
]

export function Settings() {
  const me = useMe()
  const isAdmin = me.data?.role === 'admin'
  const canSeeAudit = canViewExecutive(me.data?.role)
  const [section, setSection] = useState<SectionId>('organizations')

  return (
    <div className="two-pane" style={{ height: '100%' }}>
      <aside className="left">
        <div
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--fg-4)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '4px 8px 6px',
          }}
        >
          Налаштування
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SECTIONS.filter((s) => !s.adminOnly || isAdmin || (s.id === 'audit-log' && canSeeAudit)).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={`nav-item ${section === s.id ? 'active' : ''}`}
              style={{ height: 28 }}
            >
              <Icon name={s.icon} size={14} />
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      </aside>
      <main className="right" style={{ padding: 20, overflow: 'auto' }}>
        {!isAdmin && (
          <div
            style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 6,
              marginBottom: 16,
              color: 'var(--fg-3)',
              fontSize: 13,
            }}
          >
            Режим перегляду — редагування доступне адміністраторам.
          </div>
        )}
        {section === 'organizations' && <OrganizationsPane isAdmin={isAdmin} />}
        {section === 'accounts' && <AccountsPane isAdmin={isAdmin} />}
        {section === 'categories' && <CategoriesPane isAdmin={isAdmin} />}
        {section === 'projects' && <ProjectsPane isAdmin={isAdmin} />}
        {section === 'tags' && <TagsPane isAdmin={isAdmin} />}
        {section === 'counterparties' && <CounterpartiesPane isAdmin={isAdmin} />}
        {section === 'employees' && <EmployeesPane isAdmin={isAdmin} />}
        {section === 'fx-rates' && <FxRatesPane isAdmin={isAdmin} />}
        {section === 'license-tariffs' && <LicenseTariffsPane isAdmin={isAdmin} />}
        {section === 'telegram' && <TelegramSection />}
        {section === 'audit-log' && canSeeAudit && <AuditLogSection />}
        {section === 'client-errors' && isAdmin && <ClientErrorsSection />}
      </main>
    </div>
  )
}

function PaneHeader({
  title,
  action,
}: {
  title: string
  action?: ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
      }}
    >
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{title}</h2>
      {action}
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 12,
        color: 'var(--fg-3)',
      }}
    >
      <span>{label}</span>
      {children}
      {hint && <span style={{ color: 'var(--fg-4)', fontSize: 11 }}>{hint}</span>}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null
  const msg = error instanceof Error ? error.message : String(error)
  return (
    <div style={{ color: 'var(--danger-9)', fontSize: 12, marginTop: 8 }}>{msg}</div>
  )
}

// ---------- Organizations ----------

function OrganizationsPane({ isAdmin }: { isAdmin: boolean }) {
  const list = useOrganizations()
  const create = useCreateOrganization()
  const update = useUpdateOrganization()
  const remove = useDeleteOrganization()
  const [editing, setEditing] = useState<Organization | 'new' | null>(null)

  return (
    <>
      <PaneHeader
        title="Організації"
        action={
          isAdmin && (
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
              Додати
            </Button>
          )
        }
      />
      <ResourceTable
        items={list.data}
        loading={list.isLoading}
        columns={[
          { key: 'name', label: 'Назва' },
          { key: 'legalName', label: 'Юр. назва' },
          { key: 'taxId', label: 'EDRPOU/ITN' },
        ]}
        onEdit={isAdmin ? (row) => setEditing(row) : undefined}
        onDelete={
          isAdmin
            ? (row) => {
                void confirmDialog({
                  title: `Видалити організацію "${row.name}"?`,
                  body: 'Цю дію не можна скасувати.',
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) remove.mutate(row.id)
                })
              }
            : undefined
        }
      />
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Нова організація' : 'Редагування організації'}
          onClose={() => setEditing(null)}
          footer={null}
        >
          <OrgForm
            initial={editing === 'new' ? null : editing}
            onSubmit={async (data) => {
              if (editing === 'new') {
                await create.mutateAsync(data)
              } else {
                await update.mutateAsync({ id: editing.id, patch: data })
              }
              setEditing(null)
            }}
            error={create.error || update.error}
            busy={create.isPending || update.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function OrgForm({
  initial,
  onSubmit,
  error,
  busy,
}: {
  initial: Organization | null
  onSubmit: (data: {
    name: string
    legalName: string | null
    taxId: string | null
    b24Id: string | null
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [legalName, setLegalName] = useState(initial?.legalName ?? '')
  const [taxId, setTaxId] = useState(initial?.taxId ?? '')
  const [b24Id, setB24Id] = useState(initial?.b24Id ?? '')

  const errorMessage =
    error instanceof Error && error.message.includes('b24_id_taken')
      ? new Error('Цей B24 ID вже використовується іншою організацією')
      : error

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({
          name: name.trim(),
          legalName: legalName.trim() || null,
          taxId: taxId.trim() || null,
          b24Id: b24Id.trim() || null,
        })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Назва *">
        <input
          style={inputStyle}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Юридична назва">
        <input
          style={inputStyle}
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
        />
      </Field>
      <Field label="EDRPOU / Tax ID">
        <input style={inputStyle} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
      </Field>
      <Field
        label="B24 ID"
        hint="Потрібен для маршрутизації invoice-вебхуків з Bitrix24."
      >
        <input
          style={inputStyle}
          value={b24Id}
          onChange={(e) => setB24Id(e.target.value)}
          placeholder="ID цієї організації в Bitrix24"
        />
      </Field>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={errorMessage} />
    </form>
  )
}

// ---------- Accounts ----------

const ACCOUNT_TYPES: { id: AccountType; label: string }[] = [
  { id: 'bank', label: 'Банк' },
  { id: 'cash', label: 'Готівка' },
  { id: 'petty', label: 'Підзвіт' },
]
const CURRENCIES: Currency[] = ['UAH', 'USD', 'EUR']

function AccountsPane({ isAdmin }: { isAdmin: boolean }) {
  const list = useAccounts()
  const orgs = useOrganizations()
  const create = useCreateAccount()
  const update = useUpdateAccount()
  const remove = useDeleteAccount()
  const [editing, setEditing] = useState<Account | 'new' | null>(null)

  const orgName = (id: string) => orgs.data?.find((o) => o.id === id)?.name ?? id

  return (
    <>
      <PaneHeader
        title="Рахунки"
        action={
          isAdmin && (
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
              Додати
            </Button>
          )
        }
      />
      <ResourceTable
        items={list.data}
        loading={list.isLoading}
        columns={[
          { key: 'name', label: 'Назва' },
          {
            key: 'organizationId',
            label: 'Організація',
            render: (row) => orgName((row as Account).organizationId),
          },
          { key: 'type', label: 'Тип' },
          { key: 'currency', label: 'Валюта' },
          {
            key: 'openingBalance',
            label: 'Початковий залишок',
            render: (row) => (
              <Money
                amount={(row as Account).openingBalance}
                currency={(row as Account).currency}
                align="left"
              />
            ),
          },
          {
            key: 'isArchived',
            label: 'Архів',
            render: (row) => ((row as Account).isArchived ? 'так' : ''),
          },
        ]}
        onEdit={isAdmin ? (row) => setEditing(row) : undefined}
        onDelete={
          isAdmin
            ? (row) => {
                void confirmDialog({
                  title: `Видалити рахунок "${row.name}"?`,
                  body: 'Цю дію не можна скасувати.',
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) remove.mutate(row.id)
                })
              }
            : undefined
        }
      />
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Новий рахунок' : 'Редагування рахунку'}
          onClose={() => setEditing(null)}
        >
          <AccountForm
            initial={editing === 'new' ? null : editing}
            organizations={orgs.data ?? []}
            onSubmit={async (data) => {
              if (editing === 'new') {
                await create.mutateAsync(data)
              } else {
                const { organizationId: _omit, ...patch } = data
                void _omit
                await update.mutateAsync({ id: editing.id, patch })
              }
              setEditing(null)
            }}
            error={create.error || update.error}
            busy={create.isPending || update.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function AccountForm({
  initial,
  organizations,
  onSubmit,
  error,
  busy,
}: {
  initial: Account | null
  organizations: Organization[]
  onSubmit: (data: {
    organizationId: string
    name: string
    type: AccountType
    currency: Currency
    openingBalance: number
    openingBalanceDate: string
    isArchived?: boolean
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [organizationId, setOrganizationId] = useState(
    initial?.organizationId ?? organizations[0]?.id ?? '',
  )
  const [name, setName] = useState(initial?.name ?? '')
  const [type, setType] = useState<AccountType>(initial?.type ?? 'bank')
  const [currency, setCurrency] = useState<Currency>(initial?.currency ?? 'UAH')
  const [openingBalance, setOpeningBalance] = useState(String(initial?.openingBalance ?? 0))
  const [openingBalanceDate, setOpeningBalanceDate] = useState(
    initial?.openingBalanceDate ?? new Date().toISOString().slice(0, 10),
  )
  const [isArchived, setIsArchived] = useState(initial?.isArchived ?? false)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({
          organizationId,
          name: name.trim(),
          type,
          currency,
          openingBalance: Number(openingBalance) || 0,
          openingBalanceDate,
          isArchived,
        })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Організація *">
        <select
          style={inputStyle}
          required
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          disabled={!!initial}
        >
          {organizations.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Назва *">
        <input
          style={inputStyle}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Тип *">
        <select
          style={inputStyle}
          value={type}
          onChange={(e) => setType(e.target.value as AccountType)}
        >
          {ACCOUNT_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Валюта *">
        <select
          style={inputStyle}
          value={currency}
          onChange={(e) => setCurrency(e.target.value as Currency)}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Початковий залишок">
        <AmountInput
          style={inputStyle}
          decimals={2}
          value={openingBalance}
          onChange={setOpeningBalance}
        />
      </Field>
      <Field label="Дата початкового залишку *">
        <DatePicker
          style={inputStyle}
          value={openingBalanceDate}
          onChange={setOpeningBalanceDate}
        />
      </Field>
      {initial && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isArchived}
            onChange={(e) => setIsArchived(e.target.checked)}
          />
          В архіві
        </label>
      )}
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={error} />
    </form>
  )
}

// ---------- Categories ----------

const CATEGORY_KINDS: { id: CategoryKind; label: string }[] = [
  { id: 'income', label: 'Дохід' },
  { id: 'expense', label: 'Витрата' },
  { id: 'transfer', label: 'Переказ' },
]

function CategoriesPane({ isAdmin }: { isAdmin: boolean }) {
  const list = useCategories()
  const create = useCreateCategory()
  const update = useUpdateCategory()
  const remove = useDeleteCategory()
  const [editing, setEditing] = useState<Category | 'new' | null>(null)

  return (
    <>
      <PaneHeader
        title="Категорії"
        action={
          isAdmin && (
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
              Додати
            </Button>
          )
        }
      />
      <ResourceTable
        items={list.data}
        loading={list.isLoading}
        columns={[
          { key: 'name', label: 'Назва' },
          { key: 'kind', label: 'Тип' },
          { key: 'code', label: 'Код' },
          {
            key: 'isSystem',
            label: 'Системна',
            render: (row) => ((row as Category).isSystem ? 'так' : ''),
          },
        ]}
        onEdit={isAdmin ? (row) => setEditing(row) : undefined}
        onDelete={
          isAdmin
            ? (row) => {
                const cat = row as Category
                if (cat.isSystem) {
                  void confirmDialog({
                    title: 'Системну категорію не можна видалити',
                    body: 'Системні категорії потрібні для коректної роботи переказів і початкових залишків.',
                    confirmLabel: 'Зрозуміло',
                    cancelLabel: '',
                  })
                  return
                }
                void confirmDialog({
                  title: `Видалити категорію "${cat.name}"?`,
                  body: 'Цю дію не можна скасувати.',
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) remove.mutate(cat.id)
                })
              }
            : undefined
        }
      />
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Нова категорія' : 'Редагування категорії'}
          onClose={() => setEditing(null)}
        >
          <CategoryForm
            initial={editing === 'new' ? null : editing}
            allCategories={list.data ?? []}
            onSubmit={async (data) => {
              if (editing === 'new') {
                await create.mutateAsync(data)
              } else {
                const { kind: _omit, ...patch } = data
                void _omit
                await update.mutateAsync({ id: editing.id, patch })
              }
              setEditing(null)
            }}
            error={create.error || update.error}
            busy={create.isPending || update.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function CategoryForm({
  initial,
  allCategories,
  onSubmit,
  error,
  busy,
}: {
  initial: Category | null
  allCategories: Category[]
  onSubmit: (data: {
    kind: CategoryKind
    name: string
    parentId: string | null
    code: string | null
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [kind, setKind] = useState<CategoryKind>(initial?.kind ?? 'expense')
  const [name, setName] = useState(initial?.name ?? '')
  const [parentId, setParentId] = useState(initial?.parentId ?? '')
  const [code, setCode] = useState(initial?.code ?? '')

  const candidateParents = allCategories.filter(
    (c) => c.kind === kind && c.id !== initial?.id,
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({
          kind,
          name: name.trim(),
          parentId: parentId || null,
          code: code.trim() || null,
        })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Тип *">
        <select
          style={inputStyle}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as CategoryKind)
            setParentId('')
          }}
          disabled={!!initial}
        >
          {CATEGORY_KINDS.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Назва *">
        <input
          style={inputStyle}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Батьківська категорія">
        <SearchSelect
          style={inputStyle}
          value={parentId}
          onChange={setParentId}
          emptyOption
          emptyLabel="(немає)"
          placeholder="(немає)"
          options={candidateParents.map((c) => ({ id: c.id, label: c.name }))}
        />
      </Field>
      <Field label="Код">
        <input style={inputStyle} value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={error} />
    </form>
  )
}

// ---------- Projects ----------

function ProjectsPane({ isAdmin }: { isAdmin: boolean }) {
  const list = useProjects()
  const orgs = useOrganizations()
  const create = useCreateProject()
  const update = useUpdateProject()
  const remove = useDeleteProject()
  const [editing, setEditing] = useState<Project | 'new' | null>(null)
  const orgName = (id: string | null) =>
    id === null ? '(спільний)' : orgs.data?.find((o) => o.id === id)?.name ?? id

  return (
    <>
      <PaneHeader
        title="Проєкти"
        action={
          isAdmin && (
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
              Додати
            </Button>
          )
        }
      />
      <ResourceTable
        items={list.data}
        loading={list.isLoading}
        columns={[
          { key: 'name', label: 'Назва' },
          {
            key: 'organizationId',
            label: 'Організація',
            render: (row) => orgName((row as Project).organizationId),
          },
          { key: 'code', label: 'Код' },
        ]}
        onEdit={isAdmin ? (row) => setEditing(row) : undefined}
        onDelete={
          isAdmin
            ? (row) => {
                void confirmDialog({
                  title: `Видалити проєкт "${row.name}"?`,
                  body: 'Цю дію не можна скасувати.',
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) remove.mutate(row.id)
                })
              }
            : undefined
        }
      />
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Новий проєкт' : 'Редагування проєкту'}
          onClose={() => setEditing(null)}
        >
          <ProjectForm
            initial={editing === 'new' ? null : editing}
            organizations={orgs.data ?? []}
            allProjects={list.data ?? []}
            onSubmit={async (data) => {
              if (editing === 'new') {
                await create.mutateAsync(data)
              } else {
                await update.mutateAsync({ id: editing.id, patch: data })
              }
              setEditing(null)
            }}
            error={create.error || update.error}
            busy={create.isPending || update.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function ProjectForm({
  initial,
  organizations,
  allProjects,
  onSubmit,
  error,
  busy,
}: {
  initial: Project | null
  organizations: Organization[]
  allProjects: Project[]
  onSubmit: (data: {
    name: string
    organizationId: string | null
    parentId: string | null
    code: string | null
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [organizationId, setOrganizationId] = useState(initial?.organizationId ?? '')
  const [parentId, setParentId] = useState(initial?.parentId ?? '')
  const [code, setCode] = useState(initial?.code ?? '')

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({
          name: name.trim(),
          organizationId: organizationId || null,
          parentId: parentId || null,
          code: code.trim() || null,
        })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Назва *">
        <input
          style={inputStyle}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Організація">
        <select
          style={inputStyle}
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
        >
          <option value="">(спільний для всіх)</option>
          {organizations.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Батьківський проєкт">
        <select
          style={inputStyle}
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">(немає)</option>
          {allProjects
            .filter((p) => p.id !== initial?.id)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
      </Field>
      <Field label="Код">
        <input style={inputStyle} value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={error} />
    </form>
  )
}

// ---------- Tags ----------

function TagsPane({ isAdmin }: { isAdmin: boolean }) {
  const list = useTags()
  const create = useCreateTag()
  const update = useUpdateTag()
  const remove = useDeleteTag()
  const [editing, setEditing] = useState<Tag | 'new' | null>(null)

  return (
    <>
      <PaneHeader
        title="Теги"
        action={
          isAdmin && (
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
              Додати
            </Button>
          )
        }
      />
      <ResourceTable
        items={list.data}
        loading={list.isLoading}
        columns={[
          {
            key: 'name',
            label: 'Назва',
            render: (row) => {
              const tag = row as Tag
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {tag.color && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: tag.color,
                      }}
                    />
                  )}
                  {tag.name}
                </span>
              )
            },
          },
          { key: 'color', label: 'Колір' },
        ]}
        onEdit={isAdmin ? (row) => setEditing(row) : undefined}
        onDelete={
          isAdmin
            ? (row) => {
                void confirmDialog({
                  title: `Видалити тег "${row.name}"?`,
                  body: 'Цю дію не можна скасувати.',
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) remove.mutate(row.id)
                })
              }
            : undefined
        }
      />
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Новий тег' : 'Редагування тега'}
          onClose={() => setEditing(null)}
        >
          <TagForm
            initial={editing === 'new' ? null : editing}
            onSubmit={async (data) => {
              if (editing === 'new') {
                await create.mutateAsync(data)
              } else {
                await update.mutateAsync({ id: editing.id, patch: data })
              }
              setEditing(null)
            }}
            error={create.error || update.error}
            busy={create.isPending || update.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function TagForm({
  initial,
  onSubmit,
  error,
  busy,
}: {
  initial: Tag | null
  onSubmit: (data: { name: string; color: string | null }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [color, setColor] = useState(initial?.color ?? '#22c55e')

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({ name: name.trim(), color: color || null })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Назва *">
        <input
          style={inputStyle}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Колір">
        <input
          style={{ ...inputStyle, height: 32 }}
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
        />
      </Field>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={error} />
    </form>
  )
}

// ---------- Counterparties ----------

const COUNTERPARTY_TYPES: { id: CounterpartyType; label: string }[] = [
  { id: 'customer', label: 'Клієнт' },
  { id: 'vendor', label: 'Постачальник' },
  { id: 'both', label: 'Обидва' },
]

function CounterpartiesPane({ isAdmin }: { isAdmin: boolean }) {
  const list = useCounterparties()
  const create = useCreateCounterparty()
  const update = useUpdateCounterparty()
  const remove = useDeleteCounterparty()
  const [editing, setEditing] = useState<Counterparty | 'new' | null>(null)

  return (
    <>
      <PaneHeader
        title="Контрагенти"
        action={
          isAdmin && (
            <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing('new')}>
              Додати
            </Button>
          )
        }
      />
      <ResourceTable
        items={list.data}
        loading={list.isLoading}
        columns={[
          { key: 'name', label: 'Назва' },
          { key: 'type', label: 'Тип' },
          { key: 'source', label: 'Джерело' },
          { key: 'taxId', label: 'EDRPOU/ITN' },
        ]}
        onEdit={isAdmin ? (row) => setEditing(row) : undefined}
        onDelete={
          isAdmin
            ? (row) => {
                void confirmDialog({
                  title: `Видалити "${row.name}"?`,
                  body: 'Цю дію не можна скасувати.',
                  confirmLabel: 'Видалити',
                  tone: 'danger',
                }).then((ok) => {
                  if (ok) remove.mutate(row.id)
                })
              }
            : undefined
        }
      />
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Новий контрагент' : 'Редагування контрагента'}
          onClose={() => setEditing(null)}
        >
          <CounterpartyForm
            initial={editing === 'new' ? null : editing}
            onSubmit={async (data) => {
              if (editing === 'new') {
                await create.mutateAsync({
                  name: data.name,
                  type: data.type,
                  taxId: data.taxId,
                  notes: data.notes,
                })
              } else {
                if (editing.source === 'b24') {
                  await update.mutateAsync({
                    id: editing.id,
                    patch: { notes: data.notes },
                  })
                } else {
                  await update.mutateAsync({ id: editing.id, patch: data })
                }
              }
              setEditing(null)
            }}
            error={create.error || update.error}
            busy={create.isPending || update.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function CounterpartyForm({
  initial,
  onSubmit,
  error,
  busy,
}: {
  initial: Counterparty | null
  onSubmit: (data: {
    name: string
    type: CounterpartyType
    taxId: string | null
    notes: string | null
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [type, setType] = useState<CounterpartyType>(initial?.type ?? 'customer')
  const [taxId, setTaxId] = useState(initial?.taxId ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const b24Locked = initial?.source === 'b24'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({
          name: name.trim(),
          type,
          taxId: taxId.trim() || null,
          notes: notes.trim() || null,
        })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {b24Locked && (
        <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
          Контрагент із Bitrix24 — редагувати можна тільки нотатки.
        </div>
      )}
      <Field label="Назва *">
        <input
          style={inputStyle}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={b24Locked}
        />
      </Field>
      <Field label="Тип *">
        <select
          style={inputStyle}
          value={type}
          onChange={(e) => setType(e.target.value as CounterpartyType)}
          disabled={b24Locked}
        >
          {COUNTERPARTY_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="EDRPOU / Tax ID">
        <input
          style={inputStyle}
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          disabled={b24Locked}
        />
      </Field>
      <Field label="Нотатки">
        <textarea
          style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit' }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={error} />
    </form>
  )
}

// ---------- Employees ----------

function EmployeesPane({ isAdmin }: { isAdmin: boolean }) {
  const [showInactive, setShowInactive] = useState(false)
  const list = useEmployees(showInactive ? {} : { isActive: true })
  const orgs = useOrganizations()
  const create = useCreateEmployee()
  const patch = usePatchEmployee()
  const remove = useDeleteEmployee()
  const { setToast } = useAppState()
  const [editing, setEditing] = useState<Employee | 'new' | null>(null)

  const orgName = (id: string) => orgs.data?.find((o) => o.id === id)?.name ?? id
  const items = [...flattenEmployees(list.data)].sort((a, b) =>
    a.fullName.localeCompare(b.fullName, 'uk'),
  )

  return (
    <>
      <PaneHeader
        title="Співробітники"
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--fg-3)',
              }}
            >
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Показати неактивних
            </label>
            {isAdmin && (
              <Button
                variant="primary"
                size="sm"
                icon="plus"
                onClick={() => setEditing('new')}
              >
                Додати
              </Button>
            )}
          </div>
        }
      />
      {list.isLoading ? (
        <div style={{ color: 'var(--fg-3)' }}>Завантаження…</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--fg-3)' }}>Немає записів</div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                {['ПІБ', 'Посада', 'Організація', 'Активний'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: 'var(--fg-3)',
                    }}
                  >
                    {label}
                  </th>
                ))}
                {isAdmin && <th style={{ width: 100 }} />}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const dim = !row.isActive
                const cellStyle: React.CSSProperties = {
                  padding: '8px 12px',
                  opacity: dim ? 0.55 : 1,
                }
                return (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={cellStyle}>{row.fullName}</td>
                    <td style={cellStyle}>{row.position ?? '—'}</td>
                    <td style={cellStyle}>{orgName(row.organizationId)}</td>
                    <td style={cellStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          background: row.isActive
                            ? 'var(--accent-bg, var(--bg-2))'
                            : 'var(--bg-2)',
                          color: row.isActive ? 'var(--accent, var(--fg-1))' : 'var(--fg-3)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        {row.isActive ? 'Так' : 'Ні'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(row)}
                        >
                          Редагувати
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            void confirmDialog({
                              title: `Видалити співробітника ${row.fullName}?`,
                              body: 'Цю дію не можна скасувати.',
                              confirmLabel: 'Видалити',
                              tone: 'danger',
                            }).then((ok) => {
                              if (!ok) return
                              remove.mutate(row.id, {
                                onSuccess: () => setToast('Видалено'),
                                onError: () => setToast('Помилка'),
                              })
                            })
                          }}
                        >
                          Видалити
                        </Button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <Drawer
          title={editing === 'new' ? 'Новий співробітник' : 'Редагування співробітника'}
          onClose={() => setEditing(null)}
        >
          <EmployeeForm
            initial={editing === 'new' ? null : editing}
            organizations={orgs.data ?? []}
            onSubmit={async (data) => {
              try {
                if (editing === 'new') {
                  await create.mutateAsync(data)
                  setToast('Створено')
                } else {
                  // organizationId is immutable on backend — omit from patch
                  await patch.mutateAsync({
                    id: editing.id,
                    patch: {
                      fullName: data.fullName,
                      position: data.position,
                      isActive: data.isActive,
                    },
                  })
                  setToast('Збережено')
                }
                setEditing(null)
              } catch {
                setToast('Помилка')
              }
            }}
            error={create.error || patch.error}
            busy={create.isPending || patch.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function EmployeeForm({
  initial,
  organizations,
  onSubmit,
  error,
  busy,
}: {
  initial: Employee | null
  organizations: Organization[]
  onSubmit: (data: {
    organizationId: string
    fullName: string
    position: string | null
    isActive: boolean
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [organizationId, setOrganizationId] = useState(
    initial?.organizationId ?? organizations[0]?.id ?? '',
  )
  const [fullName, setFullName] = useState(initial?.fullName ?? '')
  const [position, setPosition] = useState(initial?.position ?? '')
  const [isActive, setIsActive] = useState(initial?.isActive ?? true)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit({
          organizationId,
          fullName: fullName.trim(),
          position: position.trim() || null,
          isActive,
        })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Організація *">
        <select
          style={inputStyle}
          required
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          disabled={!!initial}
        >
          {organizations.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="ПІБ *">
        <input
          style={inputStyle}
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </Field>
      <Field label="Посада">
        <input
          style={inputStyle}
          value={position}
          onChange={(e) => setPosition(e.target.value)}
        />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        Активний
      </label>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={error} />
    </form>
  )
}

// ---------- FX Rates ----------

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function thirtyDaysAgo(): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 30)
  return d.toISOString().slice(0, 10)
}

function sourceLabel(s: FxRateSource): string {
  switch (s) {
    case 'monobank':
      return 'Monobank'
    case 'minfin_commercial':
      return 'Minfin'
    case 'nbu':
      return 'НБУ'
    case 'manual':
      return 'Ручний'
  }
}

function fmtRate(n: number): string {
  return n.toFixed(2)
}

function FxKpiCard({
  currency,
  query,
}: {
  currency: 'USD' | 'EUR'
  query: ReturnType<typeof useLatestFxRate>
}) {
  const { data, isLoading, error } = query
  let primary: ReactNode = '—'
  let meta: ReactNode = ' '
  if (isLoading) {
    primary = '—'
    meta = 'Завантаження…'
  } else if (error) {
    primary = 'Немає даних'
    meta = error instanceof ApiError && error.status === 404 ? 'Курсів ще немає' : 'Помилка'
  } else if (data) {
    primary = `${fmtRate(data.buyRate)} / ${fmtRate(data.sellRate)}`
    meta = `${sourceLabel(data.source)} · ${data.date}`
  }
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 12,
        minWidth: 180,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {currency} сьогодні
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {primary}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>{meta}</div>
    </div>
  )
}

function FxRateCell({
  rate,
  isAdmin,
  onDelete,
}: {
  rate: FxRate | undefined
  isAdmin: boolean
  onDelete: (rate: FxRate) => void
}) {
  if (!rate) return <>—</>
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <span>
        {fmtRate(rate.buyRate)} / {fmtRate(rate.sellRate)}
      </span>
      <span
        style={{
          display: 'inline-block',
          padding: '1px 6px',
          borderRadius: 999,
          fontSize: 11,
          color: 'var(--fg-3)',
          border: '1px solid var(--border)',
          background: 'var(--bg-2)',
        }}
      >
        {sourceLabel(rate.source)}
      </span>
      {isAdmin && rate.source === 'manual' && (
        <Button
          variant="ghost"
          size="sm"
          icon="x"
          aria-label="Видалити"
          onClick={() => onDelete(rate)}
        />
      )}
    </span>
  )
}

function FxRatesPane({ isAdmin }: { isAdmin: boolean }) {
  const latestUsd = useLatestFxRate('USD')
  const latestEur = useLatestFxRate('EUR')
  const range = useMemo(() => ({ dateFrom: thirtyDaysAgo(), dateTo: today() }), [])
  const list = useFxRates(range)
  const refresh = useRefreshFxRates()
  const create = useCreateFxRate()
  const remove = useDeleteFxRate()
  const { setToast } = useAppState()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [status, setStatus] = useState<string | null>(null)

  const grouped = new Map<string, { USD?: FxRate; EUR?: FxRate }>()
  for (const r of flattenFxRates(list.data)) {
    const slot = grouped.get(r.date) ?? {}
    slot[r.currency] = r
    grouped.set(r.date, slot)
  }
  const rows = [...grouped.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1))

  const onDelete = (rate: FxRate) => {
    void confirmDialog({
      title: 'Видалити курс?',
      body: `${rate.date} · ${rate.currency} · ${sourceLabel(rate.source)}`,
      confirmLabel: 'Видалити',
      tone: 'danger',
    }).then((ok) => {
      if (!ok) return
      remove.mutate(
        { date: rate.date, currency: rate.currency, source: rate.source },
        {
          onSuccess: () => setToast('Видалено'),
          onError: () => setToast('Помилка'),
        },
      )
    })
  }

  const onRefresh = () => {
    setStatus(null)
    refresh.mutate(undefined, {
      onSuccess: (result) => {
        setStatus(
          `Оновлено: Monobank +${result.monobank.inserted}, НБУ +${result.nbu.inserted}`,
        )
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('fx_external_unavailable')) {
          setStatus('Зовнішні джерела недоступні')
        } else {
          setStatus(`Помилка: ${msg}`)
        }
      },
    })
  }

  return (
    <>
      <PaneHeader title="Курси валют" />
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <FxKpiCard currency="USD" query={latestUsd} />
        <FxKpiCard currency="EUR" query={latestEur} />
        {isAdmin && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                icon="refresh-cw"
                onClick={onRefresh}
                disabled={refresh.isPending}
              >
                {refresh.isPending ? 'Оновлення…' : 'Оновити зараз'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon="plus"
                onClick={() => setDrawerOpen(true)}
              >
                Додати ручний курс
              </Button>
            </div>
            {status && (
              <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>{status}</div>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          fontSize: 12,
          color: 'var(--fg-3)',
          marginBottom: 8,
        }}
      >
        Останні 30 днів
      </div>

      {list.isLoading ? (
        <div style={{ color: 'var(--fg-3)' }}>Завантаження…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--fg-3)' }}>Немає записів</div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                {['Дата', 'USD', 'EUR'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: 'var(--fg-3)',
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([date, slot]) => (
                <tr key={date} style={{ borderTop: '1px solid var(--border)' }}>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {date}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <FxRateCell rate={slot.USD} isAdmin={isAdmin} onDelete={onDelete} />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <FxRateCell rate={slot.EUR} isAdmin={isAdmin} onDelete={onDelete} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && (
        <Drawer title="Новий курс валюти" onClose={() => setDrawerOpen(false)}>
          <ManualFxRateForm
            onSubmit={async (data) => {
              try {
                await create.mutateAsync(data)
                setToast('Курс збережено')
                setDrawerOpen(false)
              } catch {
                setToast('Помилка')
              }
            }}
            error={create.error}
            busy={create.isPending}
          />
        </Drawer>
      )}
    </>
  )
}

function ManualFxRateForm({
  onSubmit,
  error,
  busy,
}: {
  onSubmit: (data: {
    date: string
    currency: 'USD' | 'EUR'
    buyRate: number
    sellRate: number
  }) => Promise<void>
  error: unknown
  busy: boolean
}) {
  const [date, setDate] = useState(today())
  const [currency, setCurrency] = useState<'USD' | 'EUR'>('USD')
  const [buyRate, setBuyRate] = useState('')
  const [sellRate, setSellRate] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const buy = Number(buyRate)
        const sell = Number(sellRate)
        if (!(buy > 0) || !(sell > 0)) {
          setLocalError('Курс має бути більший за нуль')
          return
        }
        setLocalError(null)
        void onSubmit({ date, currency, buyRate: buy, sellRate: sell })
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Field label="Дата *">
        <DatePicker style={inputStyle} value={date} onChange={setDate} />
      </Field>
      <Field label="Валюта *">
        <select
          style={inputStyle}
          value={currency}
          onChange={(e) => setCurrency(e.target.value as 'USD' | 'EUR')}
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </Field>
      <Field label="Buy rate *">
        <AmountInput
          style={inputStyle}
          decimals={4}
          required
          value={buyRate}
          onChange={setBuyRate}
        />
      </Field>
      <Field label="Sell rate *">
        <AmountInput
          style={inputStyle}
          decimals={4}
          required
          value={sellRate}
          onChange={setSellRate}
        />
      </Field>
      <Button variant="primary" type="submit" disabled={busy}>
        {busy ? 'Збереження…' : 'Зберегти'}
      </Button>
      <ErrorLine error={localError ?? error} />
    </form>
  )
}

// ---------- Shared resource table ----------

type Row = { id: string; name?: string }

function ResourceTable<T extends Row>({
  items,
  loading,
  columns,
  onEdit,
  onDelete,
}: {
  items: T[] | undefined
  loading: boolean
  columns: { key: string; label: string; render?: (row: T) => ReactNode }[]
  onEdit?: (row: T) => void
  onDelete?: (row: T) => void
}) {
  if (loading) {
    return <div style={{ color: 'var(--fg-3)' }}>Завантаження…</div>
  }
  if (!items || items.length === 0) {
    return <div style={{ color: 'var(--fg-3)' }}>Немає записів</div>
  }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--bg-2)' }}>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontWeight: 500,
                  color: 'var(--fg-3)',
                }}
              >
                {c.label}
              </th>
            ))}
            {(onEdit || onDelete) && <th style={{ width: 100 }} />}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} style={{ borderTop: '1px solid var(--border)' }}>
              {columns.map((c) => {
                const value = c.render
                  ? c.render(row)
                  : ((row as unknown as Record<string, unknown>)[c.key] as ReactNode)
                return (
                  <td key={c.key} style={{ padding: '8px 12px' }}>
                    {(value as ReactNode) ?? ''}
                  </td>
                )
              })}
              {(onEdit || onDelete) && (
                <td
                  style={{
                    padding: '4px 8px',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {onEdit && (
                    <Button variant="ghost" size="sm" onClick={() => onEdit(row)}>
                      Редагувати
                    </Button>
                  )}
                  {onDelete && (
                    <Button variant="ghost" size="sm" onClick={() => onDelete(row)}>
                      Видалити
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------- License Tariffs ----------

function LicenseTariffsPane({ isAdmin }: { isAdmin: boolean }) {
  const tariffs = useLicenseTariffs({ withUsage: true })
  const allCps = useCounterparties()
  const create = useCreateLicenseTariff()
  const update = useUpdateLicenseTariff()
  const remove = useDeleteLicenseTariff()
  const [editing, setEditing] = useState<LicenseTariff | 'new' | null>(null)

  const vendors = (allCps.data ?? []).filter(
    (c) => c.type === 'vendor' || c.type === 'both',
  )

  type TariffRow = NonNullable<typeof tariffs.data>[number]
  const columns: ColumnDef<TariffRow>[] = [
    { id: 'name', header: 'Назва', cell: (t) => t.name },
    {
      id: 'iblock',
      header: 'B24 iblock',
      cell: (t) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{t.bitrixIblockId}</span>
      ),
    },
    {
      id: 'vendor',
      header: 'Вендор',
      cell: (t) =>
        (allCps.data ?? []).find((c) => c.id === t.vendorCounterpartyId)?.name ?? (
          <span className="fg3">—</span>
        ),
    },
    {
      id: 'cost',
      header: 'Cost',
      thClassName: 'num',
      cellClassName: 'num',
      cell: (t) => <Money amount={t.costPrice} currency={t.costCurrency} />,
    },
    { id: 'usage', header: 'Ліцензій', width: 90, cell: (t) => t.usageCount ?? 0 },
    ...(isAdmin
      ? [
          {
            id: 'actions',
            header: '',
            width: 110,
            pinned: true,
            cell: (t: TariffRow) => {
              const usage = t.usageCount ?? 0
              return (
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="pencil"
                    title="Редагувати"
                    onClick={() => setEditing(t)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="trash-2"
                    disabled={usage > 0}
                    title={usage > 0 ? `Використовується у ${usage} ліцензіях` : 'Видалити тариф'}
                    onClick={() => {
                      void confirmDialog({
                        title: 'Видалити тариф?',
                        body: 'Ця дія незворотна.',
                        confirmLabel: 'Видалити',
                        tone: 'danger',
                      }).then((ok) => {
                        if (ok) remove.mutate(t.id)
                      })
                    }}
                  />
                </div>
              )
            },
          } satisfies ColumnDef<TariffRow>,
        ]
      : []),
  ]

  return (
    <>
      <PaneHeader
        title="Тарифи ліцензій"
        action={
          isAdmin && (
            <Button
              variant="primary"
              size="sm"
              icon="plus"
              onClick={() => setEditing('new')}
            >
              Створити тариф
            </Button>
          )
        }
      />
      <DataTable tableKey="tariffs" columns={columns} rows={tariffs.data ?? []} rowKey={(t) => t.id} />
      {editing && (
        <TariffForm
          tariff={editing === 'new' ? null : editing}
          vendors={vendors}
          onCancel={() => setEditing(null)}
          onSubmit={async (data) => {
            try {
              if (editing === 'new') {
                await create.mutateAsync(data)
              } else {
                await update.mutateAsync({
                  id: (editing as LicenseTariff).id,
                  patch: {
                    name: data.name,
                    vendorCounterpartyId: data.vendorCounterpartyId,
                    costPrice: data.costPrice,
                    costCurrency: data.costCurrency,
                    allowOverpay: data.allowOverpay,
                  },
                })
              }
              setEditing(null)
            } catch (err) {
              window.alert(translateTariffError(err) ?? 'Помилка збереження')
            }
          }}
        />
      )}
    </>
  )
}

function TariffForm({
  tariff,
  vendors,
  onCancel,
  onSubmit,
}: {
  tariff: LicenseTariff | null
  vendors: Counterparty[]
  onCancel: () => void
  onSubmit: (data: {
    bitrixIblockId: string
    name: string
    vendorCounterpartyId: string
    costPrice: number
    costCurrency: Currency
    allowOverpay: boolean
  }) => void
}) {
  const [bitrixIblockId, setBitrixIblockId] = useState(tariff?.bitrixIblockId ?? '')
  const [name, setName] = useState(tariff?.name ?? '')
  const [vendorCounterpartyId, setVendorCounterpartyId] = useState(
    tariff?.vendorCounterpartyId ?? vendors[0]?.id ?? '',
  )
  const [costPrice, setCostPrice] = useState(
    tariff != null ? String(tariff.costPrice) : '',
  )
  const [costCurrency, setCostCurrency] = useState<Currency>(
    tariff?.costCurrency ?? 'UAH',
  )
  const [allowOverpay, setAllowOverpay] = useState<boolean>(tariff?.allowOverpay ?? false)
  const vendor = vendors.find((v) => v.id === vendorCounterpartyId)

  return (
    <Drawer
      onClose={onCancel}
      title={tariff ? 'Редагувати тариф' : 'Новий тариф'}
      width={460}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
        <label>
          <div className="kpi-eyebrow">Bitrix iblock ID</div>
          <input
            type="text"
            value={bitrixIblockId}
            onChange={(e) => setBitrixIblockId(e.target.value)}
            className="input"
            readOnly={tariff != null}
            style={
              tariff != null
                ? { background: 'var(--bg-3)', color: 'var(--fg-3)' }
                : undefined
            }
          />
        </label>
        <label>
          <div className="kpi-eyebrow">Назва</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
          />
        </label>
        <label>
          <div className="kpi-eyebrow">Вендор</div>
          <SearchSelect
            className="input"
            value={vendorCounterpartyId}
            onChange={setVendorCounterpartyId}
            placeholder="Оберіть вендора…"
            options={vendors.map((v) => ({ id: v.id, label: v.name }))}
          />
          {vendor && (
            <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
              Рахунок за замовчуванням:{' '}
              {vendor.defaultPaymentAccountId ?? 'не задано'}
            </div>
          )}
        </label>
        <label>
          <div className="kpi-eyebrow">Cost</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <AmountInput
              value={costPrice}
              onChange={setCostPrice}
              decimals={2}
              className="input num"
              style={{ flex: 1 }}
            />
            <select
              value={costCurrency}
              onChange={(e) => setCostCurrency(e.target.value as Currency)}
              className="input"
              style={{ width: 90 }}
            >
              <option value="UAH">UAH</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <input
            type="checkbox"
            checked={allowOverpay}
            onChange={(e) => setAllowOverpay(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <div>
            <div>Кастомний тариф (дозволити переплату)</div>
            <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
              Вимикає обмеження «сума ≤ залишок» при оплаті вендору. Підходить, коли
              cost — це placeholder, а реальна сума відома лише на момент платежу.
            </div>
          </div>
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Скасувати
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={
              !bitrixIblockId || !name || !vendorCounterpartyId || !costPrice
            }
            onClick={() =>
              onSubmit({
                bitrixIblockId,
                name,
                vendorCounterpartyId,
                costPrice: Number(costPrice),
                costCurrency,
                allowOverpay,
              })
            }
          >
            {tariff ? 'Зберегти' : 'Створити'}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

function translateTariffError(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  try {
    const parsed = JSON.parse(err.body) as { error?: string }
    const code = parsed.error ?? ''
    const map: Record<string, string> = {
      b24_iblock_id_taken: 'Тариф з таким Bitrix iblock ID вже існує.',
      unknown_vendor: 'Контрагент не існує або не є вендором.',
      field_immutable: 'bitrixIblockId не можна змінити.',
      in_use: 'Не можна видалити: тариф використовується.',
    }
    return map[code] ?? null
  } catch {
    return null
  }
}
